import { rankWith, isNumberControl, Resolve } from "@jsonforms/core";
import type { ControlElement } from "@jsonforms/core";
import { isAnyOfControl } from "@jsonforms/core";
import { AutocompleteControl } from "./AutocompleteControl/AutocompleteControl";
import { TextControl } from "./TextControl/TextControl";
import { NumberControl } from "./NumberControl/NumberControl";
import { UseEndpointTextControl } from "./UseEndpointTextControl/UseEndpointTextControl";
import { UseEndpointNumberControl } from "./UseEndpointNumberControl/UseEndpointNumberControl";
import { BooleanControl } from "./BooleanControl/BooleanControl";
import OneOfDropdownRenderer, {
  oneOfDropdownTester,
} from "./OneOfDropdownRenderer/OneOfDropdownRenderer";
import EnableableOneOfRenderer, {
  enableableOneOfTester,
} from "./EnableableOneOfRenderer/EnableableOneOfRenderer";
import OptionalFieldsAdder, {
  optionalFieldsAdderTester,
} from "./OptionalFieldsAdder/OptionalFieldsAdder";
import SafeAnyOfGuard from "./SafeAnyOfGuard/SafeAnyOfGuard";

// Check localStorage for debug mode
const DEBUG =
  typeof window !== "undefined" &&
  localStorage.getItem("DEBUG_JSONFORMS") === "true";

/** Wrap a tester function with debug logging */
function debugTester(
  name: string,
  rank: number,
  testerFn: (...args: any[]) => boolean | number,
) {
  return (...args: any[]): number => {
    const [uischema, _schema] = args;
    const result = testerFn(...args);
    const matched = typeof result === "number" ? result : result ? rank : -1;

    if (DEBUG && matched !== -1) {
      const scope = (uischema as ControlElement)?.scope || "unknown";

      // Color code by renderer type
      const color =
        rank >= 25
          ? "#9c27b0" // High priority (25-26) - Purple
          : rank >= 20
            ? "#ff9800" // Medium-high (20) - Orange
            : rank >= 10
              ? "#4caf50" // Medium (10) - Green
              : "#03a9f4"; // Low (2-6) - Blue

      console.log(
        `%c🎯 [${name}]%c rank=${matched} %c${scope}`,
        `font-weight: bold; color: ${color};`,
        "color: #888;",
        "color: #00bcd4; font-style: italic;",
      );
    }

    return matched;
  };
}

function hasUseEndpoint(schema: unknown): boolean {
  return Boolean(
    schema &&
    typeof schema === "object" &&
    "use_endpoint" in schema &&
    (schema as { use_endpoint?: unknown }).use_endpoint,
  );
}

/** True if schema type is or includes the given type (handles type as string or string[] after normalizeScalarAnyOf). */
function schemaTypeIs(schema: Record<string, unknown>, want: string): boolean {
  const t = schema?.type;
  if (t === want) return true;
  if (Array.isArray(t)) return t.includes(want);
  return false;
}

/** True if branch is an object schema (or $ref to one). Used so SafeAnyOfGuard only matches signal_parameters-style anyOf, not hedging_accounts (array|null). */
function isObjectSchemaBranch(
  branch: unknown,
  root: Record<string, unknown> | undefined,
): boolean {
  if (!branch || typeof branch !== "object") return false;
  const b = branch as Record<string, unknown>;
  if (b.$ref && typeof b.$ref === "string" && root) {
    try {
      const resolved = Resolve.schema(
        root as Parameters<typeof Resolve.schema>[0],
        b.$ref,
        root as Parameters<typeof Resolve.schema>[2],
      ) as Record<string, unknown> | undefined;
      return isObjectSchemaBranch(resolved, root);
    } catch {
      return false;
    }
  }
  return (
    b.type === "object" ||
    Boolean(b.properties && typeof b.properties === "object")
  );
}

/** Resolve control schema from root so nested fields (e.g. beta) see their real schema with use_endpoint. */
function resolveSchemaForControl(
  uischema: unknown,
  schema: unknown,
  context: { rootSchema?: unknown } | undefined,
): Record<string, unknown> | undefined {
  const control = uischema as ControlElement;
  if (control?.type !== "Control" || !control.scope) return undefined;
  const root = context?.rootSchema ?? schema;
  if (!root || typeof root !== "object") return undefined;
  try {
    const scope = control.scope.startsWith("#")
      ? control.scope
      : `#/properties/${String(control.scope).replace(/\./g, "/properties/")}`;
    return Resolve.schema(
      root as Parameters<typeof Resolve.schema>[0],
      scope,
      root as Parameters<typeof Resolve.schema>[2],
    ) as Record<string, unknown> | undefined;
  } catch {
    return undefined;
  }
}

/** Get enum array from schema, following allOf and $ref (e.g. hedge_type: allOf [ { $ref: "#/definitions/HedgeType" } ]). */
function getEnumFromSchema(
  schema: Record<string, unknown> | undefined,
  root: Record<string, unknown> | undefined,
): unknown[] | undefined {
  if (!schema || typeof schema !== "object") return undefined;
  const e = schema.enum;
  if (Array.isArray(e)) return e;
  // Top-level $ref (e.g. nested platform/router: { $ref: "#/definitions/Platform" })
  if (schema.$ref && typeof schema.$ref === "string" && root) {
    try {
      const resolved = Resolve.schema(
        root as Parameters<typeof Resolve.schema>[0],
        schema.$ref,
        root as Parameters<typeof Resolve.schema>[2],
      ) as Record<string, unknown> | undefined;
      const fromRef = getEnumFromSchema(resolved, root);
      if (Array.isArray(fromRef)) return fromRef;
    } catch {
      /* ignore */
    }
  }
  const allOf = schema.allOf;
  if (Array.isArray(allOf)) {
    for (const item of allOf as Record<string, unknown>[]) {
      if (!item || typeof item !== "object") continue;
      if (item.$ref && typeof item.$ref === "string" && root) {
        try {
          const resolved = Resolve.schema(
            root as Parameters<typeof Resolve.schema>[0],
            item.$ref,
            root as Parameters<typeof Resolve.schema>[2],
          ) as Record<string, unknown> | undefined;
          const fromRef = getEnumFromSchema(resolved, root);
          if (Array.isArray(fromRef)) return fromRef;
        } catch {
          /* ignore */
        }
      } else if (item.enum && Array.isArray(item.enum)) {
        return item.enum as unknown[];
      }
    }
  }
  return undefined;
}

/**
 * Custom Renderer Registration
 *
 * This file registers custom JSON Forms renderers for Pulse Prime RPC schemas.
 * Renderers are selected based on "testers" - functions that return a rank (priority)
 * for a given UI schema element and data schema.
 *
 * ## Priority System (Rank)
 *
 * Higher rank = higher priority. JSON Forms tries testers from highest to lowest rank
 * and uses the first one that returns a positive rank.
 *
 * **Material Renderers (built-in):**
 * - oneOf: rank 3
 * - anyOf: rank 3
 * - Other controls: rank 1-2
 *
 * **Custom Renderers (this file):**
 * - UseEndpointTextControl: rank 26 (string + use_endpoint)
 * - UseEndpointNumberControl: rank 26 (number + use_endpoint)
 * - TextControl: rank 25 (plain string, no enum)
 * - NumberControl: rank 25 (plain number)
 * - AutocompleteControl: rank 20 (enum fields, follows allOf/$ref)
 * - BooleanControl: rank 10 (boolean/checkbox)
 * - EnableableOneOfRenderer: rank 6 (oneOf with __optionalSection flag)
 * - SafeAnyOfGuard: rank 5 (anyOf where all branches are objects)
 * - OneOfDropdownRenderer: rank 4 (overrides Material oneOf at rank 3)
 * - OptionalFieldsAdder: rank 2 (custom layout for optional fields)
 *
 * ## Tester Patterns
 *
 * ### Simple (Direct type check)
 * ```typescript
 * rankWith(10, (_uischema, schema) => schema?.type === 'boolean')
 * ```
 *
 * ### Medium (Type check + enum detection)
 * ```typescript
 * rankWith(20, (uischema, schema, context) => {
 *   const resolved = resolveSchemaForControl(uischema, schema, context) ?? schema;
 *   return Array.isArray(resolved.enum);
 * })
 * ```
 *
 * ### Complex (Schema resolution from root + following allOf/$ref)
 * For nested fields (e.g., `#/properties/signal_parameters/properties/beta`), we need
 * to resolve the schema from the root to see the full schema including `use_endpoint`.
 *
 * ```typescript
 * rankWith(26, (uischema, schema, context) => {
 *   const resolved = resolveSchemaForControl(uischema, schema, context) ?? schema;
 *   return schemaTypeIs(resolved, 'string') && hasUseEndpoint(resolved);
 * })
 * ```
 *
 * ## Schema Resolution
 *
 * Nested fields need `resolveSchemaForControl()` to see parent properties.
 *
 * Example: `signal_parameters.beta` needs to see `use_endpoint` from root:
 * ```
 * Root schema:
 * {
 *   properties: {
 *     signal_parameters: {
 *       properties: {
 *         beta: {
 *           type: "number",
 *           use_endpoint: "get_beta"  // This is what we need to see
 *         }
 *       }
 *     }
 *   }
 * }
 * ```
 *
 * Without resolution, the nested tester would only see `{ type: "number" }` and miss
 * the `use_endpoint` property, causing it to use the wrong renderer.
 *
 * ## Type Handling After Transformations
 *
 * After `normalizeScalarAnyOf` transformation, `type` may be a string OR array:
 * - Before: `{ anyOf: [{ type: "string" }, { type: "null" }] }`
 * - After: `{ type: ["string", "null"] }`
 *
 * Testers must handle both cases:
 * ```typescript
 * function schemaTypeIs(schema: Record<string, unknown>, want: string): boolean {
 *   const t = schema?.type;
 *   if (t === want) return true;
 *   if (Array.isArray(t)) return t.includes(want);
 *   return false;
 * }
 * ```
 *
 * ## Debug Mode
 *
 * When `DEBUG_JSONFORMS` is enabled, all testers are wrapped with `debugTester()`
 * which logs when a renderer matches:
 *
 * ```
 * 🎯 [UseEndpointNumberControl] rank=26 matched scope="#/properties/signal_parameters/properties/beta"
 * ```
 *
 * Enable debug mode:
 * ```js
 * localStorage.setItem('DEBUG_JSONFORMS', 'true');
 * // Reload page
 * ```
 */

// Register custom renderers
// Higher rank = higher priority (custom renderers should have rank > default material renderers)
export const customRenderers = [
  // Safe anyOf: only when every branch is object (e.g. signal_parameters). Other anyOf (hedging_accounts, etc.) use default renderer.
  {
    tester: debugTester(
      "SafeAnyOfGuard",
      5,
      rankWith(5, (uischema, schema, context): boolean => {
        if (!isAnyOfControl(uischema, schema, context)) return false;
        const anyOf = (schema as { anyOf?: unknown[] }).anyOf;
        if (!Array.isArray(anyOf) || anyOf.length === 0) return false;
        const root = context?.rootSchema as Record<string, unknown> | undefined;
        for (const branch of anyOf) {
          if (!isObjectSchemaBranch(branch, root)) return false;
        }
        return true;
      }),
    ),
    renderer: SafeAnyOfGuard,
  },
  // oneOf with Enable switch (rank 6; signal_parameters only, __optionalSection in schema)
  {
    tester: debugTester("EnableableOneOfRenderer", 6, enableableOneOfTester),
    renderer: EnableableOneOfRenderer,
  },
  // oneOf as dropdown + required-only form with "add optional field" (rank 4; overrides material oneOf rank 3)
  {
    tester: debugTester("OneOfDropdownRenderer", 4, oneOfDropdownTester),
    renderer: OneOfDropdownRenderer,
  },
  // Layout: "Add optional field" autocomplete + dynamic optional field controls (used inside OneOfDropdownRenderer)
  {
    tester: debugTester("OptionalFieldsAdder", 2, optionalFieldsAdderTester),
    renderer: OptionalFieldsAdder,
  },
  // Autocomplete for enum/select fields (rank 20). Resolve schema and follow allOf/$ref so hedge_type, platform, router, etc. match.
  {
    tester: debugTester(
      "AutocompleteControl",
      20,
      rankWith(20, (uischema, schema, context) => {
        const resolved =
          resolveSchemaForControl(uischema, schema, context) ??
          (schema as Record<string, unknown>);
        const root = context?.rootSchema as Record<string, unknown> | undefined;
        const e = getEnumFromSchema(resolved, root);
        return Array.isArray(e);
      }),
    ),
    renderer: AutocompleteControl,
  },
  // use_endpoint string (rank 26). Resolve from root so nested fields (e.g. beta) match.
  // Support type as string or string[] (e.g. ['string','null']) so normalizeScalarAnyOf-flattened schemas still match.
  {
    tester: debugTester(
      "UseEndpointTextControl",
      26,
      rankWith(26, (uischema, schema, context) => {
        const resolved =
          resolveSchemaForControl(uischema, schema, context) ??
          (schema as Record<string, unknown>);
        return (
          schemaTypeIs(resolved, "string") &&
          !(resolved?.enum && Array.isArray(resolved.enum)) &&
          hasUseEndpoint(resolved)
        );
      }),
    ),
    renderer: UseEndpointTextControl,
  },
  // use_endpoint number (rank 26). Resolve from root so nested fields (e.g. beta) match.
  // Support type as string or string[] (e.g. ['number','null']) so normalizeScalarAnyOf-flattened schemas still match.
  {
    tester: debugTester(
      "UseEndpointNumberControl",
      26,
      rankWith(26, (uischema, schema, context) => {
        const resolved =
          resolveSchemaForControl(uischema, schema, context) ??
          (schema as Record<string, unknown>);
        return (
          (schemaTypeIs(resolved, "number") ||
            schemaTypeIs(resolved, "integer")) &&
          hasUseEndpoint(resolved)
        );
      }),
    ),
    renderer: UseEndpointNumberControl,
  },
  // Plain text (rank 25): string and NOT enum. Resolve from root so nested fields (e.g. cr_token) match and get password toggle.
  {
    tester: debugTester(
      "TextControl",
      25,
      rankWith(25, (uischema, schema, context) => {
        const resolved =
          resolveSchemaForControl(uischema, schema, context) ??
          (schema as Record<string, unknown>);
        return (
          schemaTypeIs(resolved, "string") &&
          !(resolved?.enum && Array.isArray(resolved.enum))
        );
      }),
    ),
    renderer: TextControl,
  },
  // Plain number (rank 25)
  {
    tester: debugTester("NumberControl", 25, rankWith(25, isNumberControl)),
    renderer: NumberControl,
  },
  // Boolean/checkbox (rank 10)
  {
    tester: debugTester(
      "BooleanControl",
      10,
      rankWith(10, (_uischema, schema) => {
        return schema?.type === "boolean";
      }),
    ),
    renderer: BooleanControl,
  },
];
